import React, { useEffect } from 'react'
import './style.sass'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import ReleaseStatus from '../../../../constants/ReleaseStatus'
import { toast } from 'react-toastify'
import SectionHeader from '../../../common/SectionHeader'
import { getReleaseById } from '../../../../redux/actions/release'
import LoadingBlock from '../../../common/LoadingBlock'
import useFormState from '../../../../hooks/useFormState'
import PlatformSettingsForm from './PlatformSettingsForm'
import FloatingBottomActionsBar from '../../../common/FloatingBottomActionsBar'
import platformSettingValidationConfig from './PlatformSettingsForm/validationConfig'
import { getPlatformSettings, addOrUpdatePlatformSettings } from '../../../../redux/actions/publish'
import AppReleasePlatformStatus from '../../../../constants/AppReleasePlatformStatus'


const PublishSettings = () => {
  // ===== Hooks =====
  const { releaseId, platformId } = useParams()
  const history = useHistory()

  // Form states
  const platformSettingsFormState = useFormState()
  const {
    formData,
    setFormData,
    setFormErrors,
    validateForm,
    formIsDirty,
    setFormIsDirty
  } = platformSettingsFormState

  // ===== Redux state =====
  const dispatch = useDispatch()
  const release = useSelector(({ release }) => release.release)
  const isFetchingCurrentRelease = useSelector(({ release }) => release.isFetchingCurrentRelease)
  const allPlatforms = useSelector(({ publish }) => publish.allPlatforms)
  const currentAppReleasePlatform = useSelector(({ publish }) => publish.currentAppReleasePlatform)
  const currentPlatformSettings = useSelector(({ publish }) => publish.currentPlatformSettings)
  const isFetchingPlatformSettings = useSelector(({ publish }) => publish.isFetchingPlatformSettings)
  const isUpdatingPlatformSettings = useSelector(({ publish }) => publish.isUpdatingPlatformSettings)

  // ===== Side effects =====
  useEffect(() => {
    if (releaseId && release?.id?.toString() !== releaseId) {
      dispatch(getReleaseById(releaseId))
    }
  }, [releaseId])

  useEffect(() => {
    if (releaseId && platformId) {
      dispatch(getPlatformSettings(releaseId, platformId))
    }
  }, [releaseId, platformId])

  useEffect(() => {
    // Initializing form data
    if (!isFetchingPlatformSettings) {
      initializeFormData()
    }
  }, [isFetchingPlatformSettings, currentPlatformSettings])

  // ===== Methods =====
  const initializeFormData = () => {
    setFormData({
      isPrivate: currentPlatformSettings?.isPrivate || false,
      productStatus: currentPlatformSettings?.productStatus,
      productType: currentPlatformSettings?.productType,
      elsaContractTypes: currentPlatformSettings?.elsaContractTypes?.split(',')?.map(x => ({ value: parseInt(x) })) || [],
    })
  }

  const submitForm = async () => {
    const isFormValid = validateForm(platformSettingValidationConfig)
    if (!isFormValid) {
      toast.error('Please fix all validation issues!')
      return
    }
    const ids = {
      appReleasePlatformId: currentAppReleasePlatform?.id || 0,
      releaseId, 
      platformId, 
      applicationId: release?.applicationId,
      applicationPlatformSettingId: currentPlatformSettings?.id || 0,
    }
    const updatedSettings = {
      id: currentPlatformSettings?.id || 0,
      ...formData,
      elsaContractTypes: formData?.elsaContractTypes?.map(x => x.value)?.join(',')
    }
    const res = await dispatch(addOrUpdatePlatformSettings(ids, updatedSettings))

    if (res) {
      toast.success('Release successfully updated!')
      setFormIsDirty(false)
      return
    }
    toast.error('Something went wrong, Try again!')
  }

  const resetAllFormUpdates = () => {
    initializeFormData()
    setFormIsDirty(false)
    setFormErrors({})
  }

  const getPlatformName = () => {
    const platform = allPlatforms?.find(x => x.id?.toString() === platformId)
    return platform?.name || 'N/A'
  }

  const getApplication = () => {
    // Parsing release.data to get the application object
    let app = null
    try {
      app = release?.data ? JSON.parse(release?.data) : null
    } catch (e) {
      /* Fail Silently */
    }
    return app
  }

  const renderFloatingActionBar = () => {
    if (formIsDirty) {
      return (
        <FloatingBottomActionsBar
          mainText={`You have some unsaved changes, please save or click cancel to revert changes.`}
          // Primary action
          primaryButtonText={currentAppReleasePlatform?.releasePlatformStatus === AppReleasePlatformStatus.PUBLISHED ? "Save and publish" : "Save"}
          onPrimaryButtonPressed={submitForm}
          isProccessingPrimaryAction={isUpdatingPlatformSettings}
          // Secondary action
          secondaryButtonText="Cancel"
          onSecondaryButtonPressed={resetAllFormUpdates}
        />
      )
    }
  }

  if (
    isFetchingPlatformSettings || 
    isFetchingCurrentRelease || 
    !release || 
    release?.id?.toString() !== releaseId
  ) {
    return <LoadingBlock message="Loading..." />
  }

  return (
    <div className="publish-settings-component-container">
      <SectionHeader
        title={`${getPlatformName()} - Platform settings`}
        subTitle={`v${release?.version?.toFixed(1)} - ${getApplication()?.name}`}
        backButtonOnClick={() => { history.push(`/admin/all-applications/publish/${releaseId}`) }}
      />
      <PlatformSettingsForm
        platformId={parseInt(platformId)}
        platformName={getPlatformName()}
        formState={platformSettingsFormState}
        disabled={isUpdatingPlatformSettings || !(release?.status === ReleaseStatus.APPROVED || release.status === ReleaseStatus.LIVE)}
      />
      <div className="spacer" />
      {renderFloatingActionBar()}
    </div>
  )
}

export default PublishSettings
